import React, { useEffect, useState, useMemo } from 'react';
import { useLocation } from '../../contexts/LocationContext';
import apiClient from '../../utils/api';

type Balance = {
  item_id: string;
  item_name: string;
  category: string;
  unit: string;
  on_hand_quantity: number;
  display_average_unit_cost?: number | null;
};

type InventoryCategory = 'feed' | 'medicine' | 'other';
type FilterCategory = 'all' | 'fish_feed' | 'goat_feed' | 'fish_medicine' | 'goat_medicine' | 'other';
type MasterDataItem = { id: string; name: string; unit?: string };

const InventoryBalances: React.FC = () => {
  const { selectedLocation } = useLocation();
  const [balances, setBalances] = useState<Balance[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterCategory, setFilterCategory] = useState<FilterCategory>('all');
  const [masterGoatFeed, setMasterGoatFeed] = useState<MasterDataItem[]>([]);
  const [masterGoatMedicine, setMasterGoatMedicine] = useState<MasterDataItem[]>([]);

  // Load master data for goat feed and medicine
  useEffect(() => {
    const loadMasterData = async () => {
      if (!selectedLocation?.id) {
        setMasterGoatFeed([]);
        setMasterGoatMedicine([]);
        return;
      }
      try {
        const [goatFeed, goatMed] = await Promise.all([
          apiClient.get('/master-data/goat_feed_brands', { params: { location_id: selectedLocation.id } }),
          apiClient.get('/master-data/goat_medicine_types', { params: { location_id: selectedLocation.id } }),
        ]);
        setMasterGoatFeed(goatFeed.data || []);
        setMasterGoatMedicine(goatMed.data || []);
      } catch (e) {
        // ignore; page can still function if empty
      }
    };
    loadMasterData();
  }, [selectedLocation?.id]);

  useEffect(() => {
    const load = async () => {
      if (!selectedLocation) return;
      setLoading(true);
      try {
        const res = await apiClient.get('/inventory/balances', { params: { location_id: selectedLocation.id } });
        setBalances(res.data);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedLocation]);

  // Helper function to get category display name
  const getCategoryDisplayName = (category: string, itemName: string): string => {
    if (category === 'feed') {
      const isGoatFeed = masterGoatFeed.some(f => f.name === itemName);
      return isGoatFeed ? 'Goat Feed' : 'Fish Feed';
    }
    if (category === 'medicine') {
      const isGoatMedicine = masterGoatMedicine.some(m => m.name === itemName);
      return isGoatMedicine ? 'Goat Medicine' : 'Fish Medicine';
    }
    if (category === 'other') {
      return 'Other Items';
    }
    return category;
  };

  const getCategoryColor = (categoryDisplayName: string): string => {
    if (categoryDisplayName === 'Fish Feed' || categoryDisplayName === 'Goat Feed') {
      return 'bg-green-100 text-green-800';
    }
    if (categoryDisplayName === 'Fish Medicine' || categoryDisplayName === 'Goat Medicine') {
      return 'bg-red-100 text-red-800';
    }
    return 'bg-gray-100 text-gray-800';
  };

  // Filter balances by category
  const filteredBalances = useMemo(() => {
    if (filterCategory === 'all') return balances;
    
    return balances.filter(b => {
      // Determine category display name inline
      let categoryDisplayName = '';
      if (b.category === 'feed') {
        const isGoatFeed = masterGoatFeed.some(f => f.name === b.item_name);
        categoryDisplayName = isGoatFeed ? 'Goat Feed' : 'Fish Feed';
      } else if (b.category === 'medicine') {
        const isGoatMedicine = masterGoatMedicine.some(m => m.name === b.item_name);
        categoryDisplayName = isGoatMedicine ? 'Goat Medicine' : 'Fish Medicine';
      } else {
        categoryDisplayName = 'Other Items';
      }
      
      switch (filterCategory) {
        case 'fish_feed':
          return categoryDisplayName === 'Fish Feed';
        case 'goat_feed':
          return categoryDisplayName === 'Goat Feed';
        case 'fish_medicine':
          return categoryDisplayName === 'Fish Medicine';
        case 'goat_medicine':
          return categoryDisplayName === 'Goat Medicine';
        case 'other':
          return categoryDisplayName === 'Other Items';
        default:
          return true;
      }
    });
  }, [balances, filterCategory, masterGoatFeed, masterGoatMedicine]);

  // Calculate totals by type and category
  const totalsByType = useMemo(() => {
    const totals: Record<string, number> = {
      'Fish': 0,
      'Goat': 0,
      'Other': 0,
    };

    balances.forEach(b => {
      const value = (b.display_average_unit_cost && b.on_hand_quantity) 
        ? b.on_hand_quantity * b.display_average_unit_cost 
        : 0;
      
      // Determine category display name inline
      let categoryDisplayName = '';
      if (b.category === 'feed') {
        const isGoatFeed = masterGoatFeed.some(f => f.name === b.item_name);
        categoryDisplayName = isGoatFeed ? 'Goat Feed' : 'Fish Feed';
      } else if (b.category === 'medicine') {
        const isGoatMedicine = masterGoatMedicine.some(m => m.name === b.item_name);
        categoryDisplayName = isGoatMedicine ? 'Goat Medicine' : 'Fish Medicine';
      } else {
        categoryDisplayName = 'Other Items';
      }
      
      if (categoryDisplayName.includes('Fish')) {
        totals['Fish'] += value;
      } else if (categoryDisplayName.includes('Goat')) {
        totals['Goat'] += value;
      } else {
        totals['Other'] += value;
      }
    });

    return totals;
  }, [balances, masterGoatFeed, masterGoatMedicine]);

  const totalsByCategory = useMemo(() => {
    const totals: Record<string, number> = {
      'Fish Feed': 0,
      'Fish Medicine': 0,
      'Goat Feed': 0,
      'Goat Medicine': 0,
      'Other Items': 0,
    };

    balances.forEach(b => {
      const value = (b.display_average_unit_cost && b.on_hand_quantity) 
        ? b.on_hand_quantity * b.display_average_unit_cost 
        : 0;
      
      // Determine category display name inline
      let categoryDisplayName = '';
      if (b.category === 'feed') {
        const isGoatFeed = masterGoatFeed.some(f => f.name === b.item_name);
        categoryDisplayName = isGoatFeed ? 'Goat Feed' : 'Fish Feed';
      } else if (b.category === 'medicine') {
        const isGoatMedicine = masterGoatMedicine.some(m => m.name === b.item_name);
        categoryDisplayName = isGoatMedicine ? 'Goat Medicine' : 'Fish Medicine';
      } else {
        categoryDisplayName = 'Other Items';
      }
      
      totals[categoryDisplayName] = (totals[categoryDisplayName] || 0) + value;
    });

    return totals;
  }, [balances, masterGoatFeed, masterGoatMedicine]);

  // Calculate total value of stock in hand
  const totalValue = balances.reduce((sum, b) => {
    if (b.display_average_unit_cost && b.on_hand_quantity) {
      return sum + (b.on_hand_quantity * b.display_average_unit_cost);
    }
    return sum;
  }, 0);

  return (
    <div className="container-mobile py-4 px-4 space-y-4">
      <h2 className="text-2xl font-bold text-gray-900">Inventory Balances</h2>

      {!loading && balances.length > 0 && (
        <div className="space-y-3">
          {/* Total Value */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl shadow-sm border border-blue-100 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Value of Stock in Hand</p>
                <p className="text-2xl font-bold text-gray-900">₹{totalValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              </div>
            </div>
          </div>

          {/* Totals by Type (Fish vs Goat) */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Total Value by Type</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 mb-1">Fish</p>
                <p className="text-lg font-bold text-gray-900">₹{totalsByType['Fish'].toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Goat</p>
                <p className="text-lg font-bold text-gray-900">₹{totalsByType['Goat'].toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              </div>
            </div>
            {totalsByType['Other'] > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-xs text-gray-500 mb-1">Other Items</p>
                <p className="text-lg font-bold text-gray-900">₹{totalsByType['Other'].toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              </div>
            )}
          </div>

          {/* Totals by Category */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Total Value by Category</h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Fish Feed</span>
                <span className="text-sm font-medium text-gray-900">₹{totalsByCategory['Fish Feed'].toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Fish Medicine</span>
                <span className="text-sm font-medium text-gray-900">₹{totalsByCategory['Fish Medicine'].toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Goat Feed</span>
                <span className="text-sm font-medium text-gray-900">₹{totalsByCategory['Goat Feed'].toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Goat Medicine</span>
                <span className="text-sm font-medium text-gray-900">₹{totalsByCategory['Goat Medicine'].toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              {totalsByCategory['Other Items'] > 0 && (
                <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                  <span className="text-sm text-gray-600">Other Items</span>
                  <span className="text-sm font-medium text-gray-900">₹{totalsByCategory['Other Items'].toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {loading ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p className="text-gray-600">Loading balances...</p>
        </div>
      ) : filteredBalances.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
          <p className="text-gray-600">
            {balances.length === 0 
              ? 'No inventory items found for this location.' 
              : `No inventory items found for selected category.`}
          </p>
        </div>
      ) : (
        <>
          {/* Filter Section - Above listing */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-700 font-medium whitespace-nowrap">Filter by Category:</label>
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value as FilterCategory)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white min-w-[180px]"
                >
                  <option value="all">All Categories</option>
                  <option value="fish_feed">Fish Feed</option>
                  <option value="goat_feed">Goat Feed</option>
                  <option value="fish_medicine">Fish Medicine</option>
                  <option value="goat_medicine">Goat Medicine</option>
                  <option value="other">Other Items</option>
                </select>
              </div>
              <div className="text-sm text-gray-600">
                Showing {filteredBalances.length} of {balances.length} items
              </div>
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="block md:hidden space-y-3">
            {filteredBalances.map((b) => {
              const categoryDisplayName = getCategoryDisplayName(b.category, b.item_name);
              return (
                <div key={b.item_id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 text-base mb-1">{b.item_name}</h3>
                      <span className={`inline-block px-2 py-1 text-xs font-medium rounded ${getCategoryColor(categoryDisplayName)}`}>
                        {categoryDisplayName}
                      </span>
                    </div>
                  </div>
                <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-100">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">On Hand</p>
                    <p className="text-sm font-medium text-gray-900">{b.on_hand_quantity.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Unit</p>
                    <p className="text-sm font-medium text-gray-900">{b.unit}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-100">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Average Unit Cost</p>
                    <p className="text-sm font-medium text-gray-900">
                      {b.display_average_unit_cost ? `₹${b.display_average_unit_cost.toFixed(2)}` : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Value</p>
                    <p className="text-sm font-medium text-gray-900">
                      {b.display_average_unit_cost && b.on_hand_quantity 
                        ? `₹${(b.on_hand_quantity * b.display_average_unit_cost).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : '-'}
                    </p>
                  </div>
                </div>
              </div>
              );
            })}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Item</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Category</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">On Hand</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Unit</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Average Unit Cost</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredBalances.map((b) => {
                    const categoryDisplayName = getCategoryDisplayName(b.category, b.item_name);
                    return (
                      <tr key={b.item_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium">{b.item_name}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`inline-block px-2 py-1 text-xs font-medium rounded ${getCategoryColor(categoryDisplayName)}`}>
                            {categoryDisplayName}
                          </span>
                        </td>
                      <td className="px-4 py-3 text-sm text-gray-900">{b.on_hand_quantity.toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{b.unit}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {b.display_average_unit_cost ? `₹${b.display_average_unit_cost.toFixed(2)}` : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {b.display_average_unit_cost && b.on_hand_quantity 
                          ? `₹${(b.on_hand_quantity * b.display_average_unit_cost).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : '-'}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default InventoryBalances;


